"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate, formatToHumanReadable } from "@/lib/format-data";
import type { PatientEditLogEntry } from "@/api/provider/user/patient/patient";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
interface DemographicEditHistoryDialogProps {
  patientId: string;
  children: React.ReactNode;
}
type EditHistoryGroup = {
  key: string;
  entries: PatientEditLogEntry[];
};
function groupEditHistory(entries: PatientEditLogEntry[]): EditHistoryGroup[] {
  const groups: EditHistoryGroup[] = [];
  const indexByBatch = new Map<string, number>();
  for (const entry of entries) {
    if (entry.batchId) {
      const existing = indexByBatch.get(entry.batchId);
      if (existing !== undefined) {
        groups[existing].entries.push(entry);
        continue;
      }
      indexByBatch.set(entry.batchId, groups.length);
      groups.push({
        key: `batch:${entry.batchId}`,
        entries: [entry]
      });
    } else {
      groups.push({
        key: `entry:${entry.id}`,
        entries: [entry]
      });
    }
  }
  return groups;
}
const SOURCE_LABELS: Record<string, string> = {
  provider: "Provider",
  patient_portal: "Patient portal",
  scribe: "AI scribe",
  form: "Form",
  import: "Import",
  ai_assistant: "AI assistant",
  system: "System"
};
export default function DemographicEditHistoryDialog(t0) {
  const $ = _c(38);
  const {
    patientId,
    children
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== patientId) {
    t1 = patientQueryOptions.editHistory(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== open || $[3] !== t1) {
    t2 = {
      ...t1,
      enabled: open
    };
    $[2] = open;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: t3,
    isLoading
  } = useQuery(t2);
  let T0;
  let T1;
  let T2;
  let t10;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== children || $[6] !== isLoading || $[7] !== open || $[8] !== t3) {
    const editHistory = t3 === undefined ? [] : t3;
    T2 = Dialog;
    t8 = open;
    t9 = setOpen;
    if ($[19] !== children) {
      t10 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
      $[19] = children;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    T1 = DialogContent;
    t6 = "max-w-lg";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <DialogHeader><DialogTitle>Demographic Edit History</DialogTitle></DialogHeader>;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    T0 = DialogBody;
    t4 = "max-h-[60vh] overflow-y-auto";
    t5 = isLoading ? <div className="space-y-3">{Array.from({
        length: 3
      }).map(_temp)}</div> : editHistory.length === 0 ? <p className="py-8 text-center text-body text-foreground-secondary">No edit history found.</p> : <div className="space-y-3">{groupEditHistory(editHistory).map(_temp3)}</div>;
    $[5] = children;
    $[6] = isLoading;
    $[7] = open;
    $[8] = t3;
    $[9] = T0;
    $[10] = T1;
    $[11] = T2;
    $[12] = t10;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    T2 = $[11];
    t10 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t11;
  if ($[22] !== T0 || $[23] !== t4 || $[24] !== t5) {
    t11 = <T0 className={t4}>{t5}</T0>;
    $[22] = T0;
    $[23] = t4;
    $[24] = t5;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DialogFooter><DialogClose asChild={true}><Button variant="secondary">Close</Button></DialogClose></DialogFooter>;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== T1 || $[28] !== t11 || $[29] !== t6 || $[30] !== t7) {
    t13 = <T1 className={t6}>{t7}{t11}{t12}</T1>;
    $[27] = T1;
    $[28] = t11;
    $[29] = t6;
    $[30] = t7;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== T2 || $[33] !== t10 || $[34] !== t13 || $[35] !== t8 || $[36] !== t9) {
    t14 = <T2 open={t8} onOpenChange={t9}>{t10}{t13}</T2>;
    $[32] = T2;
    $[33] = t10;
    $[34] = t13;
    $[35] = t8;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
function _temp3(group) {
  const head = group.entries[0];
  return <div key={group.key} className="rounded-md border border-border p-3"><div className="flex items-center justify-between"><span className="text-body-strong text-foreground">{group.entries.length > 1 ? `${group.entries.length} field changes` : head.fieldName ? formatToHumanReadable(head.fieldName) : formatToHumanReadable(head.action)}</span><span className="text-caption text-foreground-secondary">{formatDate(head.createdDate)}</span></div>{group.entries.length > 1 ? <ul className="mt-1.5 space-y-1">{group.entries.map(_temp2)}</ul> : <div className="mt-1.5 flex items-center gap-2 text-caption"><span className="text-foreground-secondary line-through">{head.oldValue || "Empty"}</span><span className="text-foreground-secondary">→</span><span className="text-foreground">{head.newValue || "Empty"}</span></div>}<p className="mt-1 text-caption text-foreground-secondary"><span>{head.createdByName && `by ${head.createdByName}`}</span>{head.createdByName && head.source !== "provider" && " "}<span>{head.source !== "provider" && `via ${SOURCE_LABELS[head.source] ?? formatToHumanReadable(head.source)}${head.sourceRefName ? `: ${head.sourceRefName}` : ""}`}</span></p></div>;
}
function _temp2(entry) {
  return <li key={entry.id} className="flex flex-wrap items-center gap-2 text-caption"><span className="font-medium text-foreground">{entry.fieldName ? formatToHumanReadable(entry.fieldName) : formatToHumanReadable(entry.action)}</span><span className="text-foreground-secondary line-through">{entry.oldValue || "Empty"}</span><span className="text-foreground-secondary">→</span><span className="text-foreground">{entry.newValue || "Empty"}</span></li>;
}
function _temp(_, i) {
  return <div key={i} className="rounded-md border border-border p-3"><div className="flex items-center justify-between"><Skeleton className="h-4 w-24" /><Skeleton className="h-3 w-20" /></div><div className="mt-1.5 flex items-center gap-2"><Skeleton className="h-3 w-16" /><Skeleton className="h-3 w-16" /></div><Skeleton className="mt-1 h-3 w-28" /></div>;
}